import ODataStore from 'devextreme/data/odata/store'
import DataSource from 'devextreme/data/data_source'
import { memo, useRef } from 'react'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import type { commessa, task } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ArraySourceComposer } from '@/auth/api/connector'
import { useTokenRefresh, AzureUserInfo } from '@/auth/azure/azureManager'
import { GridCellColor } from '@/enums'
import {
  Column,
  DataGrid,
  DataGridRef,
  DataGridTypes,
  Item,
  Paging,
  StateStoring,
  Toolbar,
  Button as GridButton,
} from 'devextreme-react/data-grid'
import type { CellPreparedEvent, ColumnButtonClickEvent, RowDblClickEvent } from 'devextreme/ui/data_grid'
import notify from 'devextreme/ui/notify'
import { useScreenSize } from '@/themes/media-query'
import { ClickEvent } from 'devextreme/ui/button'
import { isUserRoleAllowed } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import { Link, useNavigate } from 'react-router-dom'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'

const ProjectEditorFormTasksGrid = (props: { project: commessa; userInfo: AzureUserInfo | undefined }) => {
  const { project, userInfo } = props
  const navigate = useNavigate()
  const currentScreenSize = useScreenSize()
  const token = useTokenRefresh()
  const gridRef = useRef<DataGridRef>(null)
  const onCellPrepared = (e: CellPreparedEvent) => {
    if (e.rowType === 'data' && e.column.dataField === 'stato.nome' && e.value) {
      switch (e.value) {
        case 'APERTO': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`

          break
        }
        case 'COMPLETATO': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.EMERALD}`

          break
        }
      }
    }
  }

  const tasksDataSource = new DataSource({
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/task`,
      key: 'id',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    filter: [['commessa.id', project.id]],
    expand: ['stato', 'resp_tecnico', 'proprietario', 'tecnici'],
  })

  return (
    <>
      <DataGrid
        id={`project-orders-grid`}
        ref={gridRef}
        dataSource={tasksDataSource}
        noDataText="Nessun task associato alla commessa"
        keyExpr={'id'}
        showBorders={true}
        showColumnLines={true}
        focusedRowEnabled={true}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnResizing={true}
        rowAlternationEnabled={true}
        wordWrapEnabled={false}
        repaintChangesOnly={true}
        width="100%"
        onCellPrepared={onCellPrepared}
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') navigate(`../../tasks/${e.data.id}`)
        }}
      >
        <StateStoring enabled={true} type="localStorage" storageKey={'project-tasks-datagrid'} savingTimeout={50} />
        <Paging defaultPageSize={8} />
        <Column
          dataField={'id'}
          caption={'ID'}
          visible={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
        />
        <Column
          dataField={'ded_Dis'}
          caption={'CODICE'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: task = cellData.row.data
            return (
              <ObjectCodeLink
                url={`../../tasks/${data.id}`}
                text={data.ded_Dis}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
          }
        />
        <Column
          dataField={'proprietario.fullname'}
          caption={'AUTORE'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
          }
        />
        <Column
          dataField={'resp_tecnico.fullname'}
          caption={'RESPONSABILE'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
          }
        />
        <Column
          dataField={'apertura'}
          dataType="date"
          caption={'APERTURA'}
          format={'dd/MM/yyyy'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
        />
        <Column
          dataField={'consegna_prevista'}
          dataType="date"
          caption={'CONSEGNA'}
          format={'dd/MM/yyyy'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
        />
        <Column
          dataField={'chiusura'}
          dataType="date"
          caption={'CHIUSURA'}
          format={'dd/MM/yyyy'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
          }
        />
        <Column
          dataField={'stato.nome'}
          caption={'STATO'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
          }
        />
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
        </Toolbar>
      </DataGrid>
    </>
  )
}

const ProjectEditorFormTasksGridMemoed = memo(ProjectEditorFormTasksGrid, (oldProps, newProps) => {
  return oldProps.project === newProps.project
})

export default ProjectEditorFormTasksGridMemoed
